import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../providers/auth-provider';
import { useTheme } from '../providers/theme-provider';
import { isCustomCellMode } from '../lib/runtime-config';
import { CommandPalette } from './CommandPalette';
import { HomeIcon } from './icons';
import { applyCustomCellMode, CUSTOM_CELL_VIEW_IDS, filterNavItems } from './nav-gate';
import { Sidebar } from './Sidebar';
import {
  allLeaves,
  collapseEmpty,
  defaultLayout,
  findLeafById,
  mapTree,
  pruneTabsByView,
  replaceLeaf,
  uid,
} from './layout';
import { loadState, saveState, SIDEBAR_DEFAULT_WIDTH } from './storage';
import { StatusBar } from './StatusBar';
import { TreeRenderer } from './TreeRenderer';
import { UserMenu } from './UserMenu';
import { getViewMeta } from './ViewRegistry';
import type {
  DragState,
  DropTarget,
  Layout,
  LeafNode,
  NavItem,
  OpenTabOptions,
  Tab,
} from './types';

interface WorkspaceProps {
  /**
   * EventTarget to attach global keyboard shortcuts (⌘K, ⌘W, etc.). In SPA
   * mode this is `window`. In Shadow DOM embed mode, pass the custom element
   * host so shortcuts don't swallow keystrokes on the embedding page.
   */
  keyTarget?: EventTarget;
}

const navItems: NavItem[] = [
  { id: 'home', icon: <HomeIcon />, label: 'Start', viewId: 'start' },
  { id: 'conversations', icon: '💬', label: 'Conversations', viewId: 'conversations' },
  { id: 'inbox', icon: '📥', label: 'Inbox', viewId: 'inbox' },
  { id: 'tasks', icon: '✓', label: 'Tasks', viewId: 'tasks' },
  {
    id: 'agents',
    icon: '🤖',
    label: 'Agents',
    viewId: 'agents',
    requireAnyCapability: ['manage_agents', 'creator_mode'],
  },
  {
    id: 'skills',
    icon: '🧩',
    label: 'Skills',
    viewId: 'skills',
    requireAnyCapability: ['creator_mode'],
  },
  {
    // Uploaded skill bundles (/skills-repository), distinct from the git-authored Skills above.
    id: 'skills-repository',
    icon: '📦',
    label: 'Skills Repository',
    viewId: 'skills-repository',
    requireAnyCapability: ['creator_mode'],
  },
  { id: 'builder', icon: '✨', label: 'Builder', viewId: 'builder' },
  { id: 'routines', icon: '🕒', label: 'Routines', viewId: 'routines' },
  { id: 'sandbox-runtime-logs', icon: '📡', label: 'Sandbox Runtime Logs', viewId: 'sandbox-runtime-logs' },
  {
    id: 'sandbox-fleet',
    icon: '🛳️',
    label: 'Sandbox Fleet',
    viewId: 'sandbox-fleet',
    // Deployment-global infrastructure; the server gates it on full_access.
    requireAnyCapability: ['full_access'],
  },
  {
    id: 'sdk-governance',
    icon: '📦',
    label: 'SDKs',
    viewId: 'sdk-governance',
    requireAnyCapability: ['full_access'],
  },
  { id: 'plans', icon: '📋', label: 'Plans', viewId: 'plans' },
  { id: 'proxy-vault', icon: '⚿', label: 'Proxy Vault', viewId: 'proxy-vault' },
  {
    id: 'git-credentials',
    icon: '🌿',
    label: 'Git Credentials',
    viewId: 'git-credentials',
    // Owner-root scoped registry; the server gates it on full_access anyway.
    requireAnyCapability: ['full_access'],
  },
  { id: 'api-keys', icon: '🗝️', label: 'API Keys', viewId: 'api-keys' },
  {
    // Operator SFTP onto tenant workspaces: uploaded keys, subtree-scoped accounts, the
    // runtime allowlist and the connection card. Each tab is gated server-side as well.
    id: 'sftp',
    icon: '🗂️',
    label: 'SFTP',
    viewId: 'sftp',
    requireAnyCapability: ['sftp_access', 'manage_users'],
  },
  {
    id: 'tenants',
    icon: '🏢',
    label: 'Organization',
    viewId: 'tenants',
    requireAnyCapability: ['manage_users'],
  },
  { id: 'users', icon: '👥', label: 'Team', viewId: 'users' },
  { id: 'roles', icon: '🛂', label: 'Roles', viewId: 'roles' },
  { id: 'changelog', icon: '📣', label: "What's new", viewId: 'changelog' },
];

// Mode-aware fresh layout: custom cell mode lands on the operational surface
// (Start is hidden there).
function modeDefaultLayout(): LeafNode {
  return isCustomCellMode()
    ? defaultLayout({ viewId: 'sandbox-runtime-logs', title: 'Sandbox Runtime Logs' })
    : defaultLayout();
}

export function Workspace({ keyTarget }: WorkspaceProps = {}) {
  const saved = useRef(
    (() => {
      const s = loadState();
      if (!s || !isCustomCellMode()) return s;
      // Restoring a persisted layout under custom cell mode: prune tabs of
      // hidden views (incl. from ⌘⇧T's reopen list) so the old Start /
      // Conversations tabs don't resurface.
      return {
        ...s,
        layout: pruneTabsByView(s.layout, CUSTOM_CELL_VIEW_IDS) ?? modeDefaultLayout(),
        recentlyClosed: s.recentlyClosed.filter((t) => CUSTOM_CELL_VIEW_IDS.has(t.viewId)),
      };
    })(),
  ).current;
  const { theme } = useTheme();
  const { user } = useAuth();
  const visibleNavItems = useMemo(
    () => applyCustomCellMode(filterNavItems(navItems, user?.capabilities ?? null), isCustomCellMode()),
    [user],
  );

  const [layout, setLayout] = useState<Layout>(saved?.layout ?? modeDefaultLayout());
  const [activeLeafId, setActiveLeafId] = useState<string | null>(() => {
    if (saved?.activeLeafId) return saved.activeLeafId;
    const initial = saved?.layout ?? modeDefaultLayout();
    return allLeaves(initial)[0]?.id ?? null;
  });
  const [activeNav, setActiveNav] = useState<string>(() =>
    isCustomCellMode() ? 'sandbox-runtime-logs' : 'home',
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(saved?.sidebarCollapsed ?? false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(saved?.sidebarWidth ?? SIDEBAR_DEFAULT_WIDTH);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [recentlyClosed, setRecentlyClosed] = useState<Tab[]>(saved?.recentlyClosed ?? []);
  const [dragState, setDragState] = useState<DragState | null>(null);

  // Ensure every leaf carries an id (old persisted state might not).
  useEffect(() => {
    setLayout((l) => {
      let changed = false;
      const mapped = mapTree(l, (leaf) => {
        if (!leaf.id) {
          changed = true;
          return { ...leaf, id: uid('leaf-') };
        }
        return leaf;
      });
      return changed ? mapped : l;
    });
  }, []);

  useEffect(() => {
    const leaves = allLeaves(layout);
    const valid = !!activeLeafId && leaves.some((l) => l.id === activeLeafId);
    if (!valid) {
      const first = leaves[0];
      if (first) setActiveLeafId(first.id);
    }
  }, [layout, activeLeafId]);

  useEffect(() => {
    saveState({ layout, activeLeafId, theme, recentlyClosed, sidebarCollapsed, sidebarWidth });
  }, [layout, activeLeafId, theme, recentlyClosed, sidebarCollapsed, sidebarWidth]);

  // ---- Tab ops ---------------------------------------------------------
  const openTabInLeaf = useCallback(
    (
      leafId: string,
      viewId: string,
      payload: Record<string, unknown> = {},
      label?: string,
      opts: OpenTabOptions = {},
    ) => {
      const meta = getViewMeta(viewId);
      const title = label ?? meta?.title ?? viewId;
      setLayout((prev) =>
        mapTree(prev, (leaf) => {
          if (leaf.id !== leafId) return leaf;

          const existing = leaf.tabs.find(
            (t) => t.viewId === viewId && JSON.stringify(t.payload ?? {}) === JSON.stringify(payload ?? {}),
          );
          if (existing) {
            return {
              ...leaf,
              activeId: existing.id,
              tabs: leaf.tabs.map((t) =>
                t.id === existing.id ? { ...t, ephemeral: opts.ephemeral ? t.ephemeral : false } : t,
              ),
            };
          }

          const newTab: Tab = {
            id: uid('tab-'),
            viewId,
            payload,
            title,
            ephemeral: !!opts.ephemeral,
            pinned: !!opts.pinned,
          };

          if (opts.ephemeral) {
            const ephIdx = leaf.tabs.findIndex((t) => t.ephemeral);
            if (ephIdx >= 0) {
              const tabs = [...leaf.tabs];
              tabs[ephIdx] = newTab;
              return { ...leaf, tabs, activeId: newTab.id };
            }
          }
          return { ...leaf, tabs: [...leaf.tabs, newTab], activeId: newTab.id };
        }),
      );
      setActiveLeafId(leafId);
    },
    [],
  );

  const openTab = useCallback(
    (viewId: string, payload?: Record<string, unknown>, label?: string, opts?: OpenTabOptions) => {
      const target = activeLeafId ?? allLeaves(layout)[0]?.id;
      if (target) openTabInLeaf(target, viewId, payload, label, opts);
    },
    [activeLeafId, layout, openTabInLeaf],
  );

  const closeTab = useCallback((leafId: string, tabId: string) => {
    setLayout((prev) => {
      const next = mapTree(prev, (leaf) => {
        if (leaf.id !== leafId) return leaf;
        const tab = leaf.tabs.find((t) => t.id === tabId);
        if (tab) setRecentlyClosed((rc) => [tab, ...rc.filter((t) => t.id !== tabId)].slice(0, 8));
        const tabs = leaf.tabs.filter((t) => t.id !== tabId);
        const activeId = leaf.activeId === tabId ? (tabs[tabs.length - 1]?.id ?? null) : leaf.activeId;
        return { ...leaf, tabs, activeId };
      });
      return collapseEmpty(next) ?? modeDefaultLayout();
    });
  }, []);

  const pinTab = useCallback((leafId: string, tabId: string) => {
    setLayout((prev) =>
      mapTree(prev, (leaf) => {
        if (leaf.id !== leafId) return leaf;
        return {
          ...leaf,
          tabs: leaf.tabs.map((t) => (t.id === tabId ? { ...t, ephemeral: false, pinned: !t.pinned } : t)),
        };
      }),
    );
  }, []);

  const activateTab = useCallback((leafId: string, tabId: string) => {
    setLayout((prev) => mapTree(prev, (leaf) => (leaf.id === leafId ? { ...leaf, activeId: tabId } : leaf)));
    setActiveLeafId(leafId);
  }, []);

  const onTabDragEnd = useCallback((sourceLeafId: string, tabId: string, target: DropTarget) => {
    setLayout((prev) => {
      const src = findLeafById(prev, sourceLeafId);
      if (!src) return prev;
      const tab = src.node.tabs.find((t) => t.id === tabId);
      if (!tab) return prev;

      // Remove from source first.
      let next: Layout = mapTree(prev, (leaf) => {
        if (leaf.id !== sourceLeafId) return leaf;
        const tabs = leaf.tabs.filter((t) => t.id !== tabId);
        const activeId = leaf.activeId === tabId ? (tabs[tabs.length - 1]?.id ?? null) : leaf.activeId;
        return { ...leaf, tabs, activeId };
      });

      if (target.type === 'leaf-tabs') {
        next = mapTree(next, (leaf) => {
          if (leaf.id !== target.leafId) return leaf;
          const tabs = [...leaf.tabs];
          const idx = Math.min(target.index ?? tabs.length, tabs.length);
          tabs.splice(idx, 0, tab);
          return { ...leaf, tabs, activeId: tab.id };
        });
        setActiveLeafId(target.leafId);
      } else {
        const targetHit = findLeafById(next, target.leafId);
        if (!targetHit) return collapseEmpty(next) ?? modeDefaultLayout();
        const targetLeaf: LeafNode = targetHit.node;
        const newLeaf: LeafNode = { type: 'leaf', id: uid('leaf-'), tabs: [tab], activeId: tab.id };
        const replacement: Layout =
          target.edge === 'left'
            ? { type: 'split', dir: 'v', a: newLeaf, b: targetLeaf, size: 0.5 }
            : target.edge === 'right'
              ? { type: 'split', dir: 'v', a: targetLeaf, b: newLeaf, size: 0.5 }
              : target.edge === 'top'
                ? { type: 'split', dir: 'h', a: newLeaf, b: targetLeaf, size: 0.5 }
                : { type: 'split', dir: 'h', a: targetLeaf, b: newLeaf, size: 0.5 };
        next = replaceLeaf(next, target.leafId, replacement);
        setActiveLeafId(newLeaf.id);
      }

      return collapseEmpty(next) ?? modeDefaultLayout();
    });
  }, []);

  const reopenLastClosed = useCallback(() => {
    if (!recentlyClosed.length) return;
    const [first, ...rest] = recentlyClosed;
    if (!first) return;
    setRecentlyClosed(rest);
    openTab(first.viewId, first.payload, first.title, { pinned: first.pinned });
  }, [recentlyClosed, openTab]);

  // ---- Keyboard shortcuts ---------------------------------------------
  // Scoped to keyTarget when provided (Shadow DOM embed: the custom element
  // host) so shortcuts don't leak onto the embedding page. Defaults to
  // window for SPA mode.
  useEffect(() => {
    const target: EventTarget = keyTarget ?? window;
    const onKey = (e: Event) => {
      const ke = e as KeyboardEvent;
      const mod = ke.metaKey || ke.ctrlKey;
      if (mod && (ke.key === 'k' || ke.key === 'p')) {
        ke.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (mod && ke.key === 'w') {
        ke.preventDefault();
        const leaves = allLeaves(layout);
        const leaf = leaves.find((l) => l.id === activeLeafId) ?? leaves[0];
        if (leaf?.activeId) closeTab(leaf.id, leaf.activeId);
      } else if (mod && ke.shiftKey && ke.key === 'T') {
        ke.preventDefault();
        reopenLastClosed();
      } else if (mod && /^[1-9]$/.test(ke.key)) {
        ke.preventDefault();
        const leaves = allLeaves(layout);
        const leaf = leaves.find((l) => l.id === activeLeafId) ?? leaves[0];
        if (leaf) {
          const idx = parseInt(ke.key, 10) - 1;
          const tab = leaf.tabs[idx];
          if (tab) activateTab(leaf.id, tab.id);
        }
      } else if (ke.key === 'Escape') {
        setPaletteOpen(false);
      }
    };
    target.addEventListener('keydown', onKey);
    return () => target.removeEventListener('keydown', onKey);
  }, [layout, activeLeafId, activateTab, closeTab, reopenLastClosed, keyTarget]);

  // ---- Nav actions -----------------------------------------------------
  const onNavOpen = useCallback(
    (item: NavItem) => {
      setActiveNav(item.id);
      openTab(item.viewId, {}, item.label, { ephemeral: false });
    },
    [openTab],
  );

  const tabCount = useMemo(() => allLeaves(layout).reduce((s, l) => s + l.tabs.length, 0), [layout]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        background: 'var(--bg)',
        color: 'var(--text)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", Helvetica, sans-serif',
      }}
    >
      <TitleBar onOpenPalette={() => setPaletteOpen(true)} />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar
          items={visibleNavItems}
          activeNav={activeNav}
          onOpen={onNavOpen}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
          width={sidebarWidth}
          onResize={setSidebarWidth}
        />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <TreeRenderer
            node={layout}
            setLayout={setLayout}
            activeLeafId={activeLeafId}
            setActiveLeafId={setActiveLeafId}
            onClose={closeTab}
            onPin={pinTab}
            onActivate={activateTab}
            onDragEnd={onTabDragEnd}
            dragState={dragState}
            setDragState={setDragState}
            openTabInLeaf={openTabInLeaf}
          />
          <StatusBar tabCount={tabCount} />
        </div>
      </div>

      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          openTab={openTab}
          layout={layout}
          activeLeafId={activeLeafId}
          closeTab={closeTab}
          navItems={visibleNavItems}
        />
      )}
    </div>
  );
}

interface TitleBarProps {
  onOpenPalette: () => void;
}

function TitleBar({ onOpenPalette }: TitleBarProps) {
  return (
    <div
      style={{
        height: 34,
        background: 'var(--bg-alt2)',
        borderBottom: '1px solid var(--border-strong)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: 10,
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#ed6a5e' }} />
        <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#f4be4f' }} />
        <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#61c554' }} />
      </div>
      <div style={{ flex: 1, textAlign: 'center', fontSize: 12, color: 'var(--text-soft)', fontWeight: 500 }}>
        ShiftAgent Workspace
      </div>
      <button
        onClick={onOpenPalette}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 10px',
          fontSize: 11,
          color: 'var(--text-soft)',
          background: 'var(--bg)',
          border: '1px solid var(--border-strong)',
          borderRadius: 6,
          cursor: 'pointer',
          minWidth: 220,
        }}
      >
        <span>🔍</span>
        <span style={{ flex: 1, textAlign: 'left' }}>Search or jump to…</span>
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'var(--text-mute)' }}>⌘K</span>
      </button>
      <UserMenu />
    </div>
  );
}
